import React, { useState, useEffect } from 'react';
import { getUsers, createUserAccount, updateUserAccount, deleteUserAccount, resetUserPassword, getSession } from '../services/api';

const styles = {
  heading: { fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '24px' },
  toolbar: { display: 'flex', gap: '12px', marginBottom: '24px' },
  addBtn: { padding: '10px 20px', background: '#9C3D97', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  th: { padding: '12px 16px', textAlign: 'left', background: '#f7fafc', fontSize: '12px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' },
  td: { padding: '12px 16px', borderTop: '1px solid #e2e8f0', fontSize: '14px', color: '#4a5568' },
  badge: { padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  badgeAdmin: { background: '#e9d8fd', color: '#6b46c1' },
  badgeGrader: { background: '#bee3f8', color: '#2b6cb0' },
  badgeDeveloper: { background: '#fefcbf', color: '#744210', border: '1px solid #ecc94b' },
  actionBtn: { padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#fff', fontSize: '13px', cursor: 'pointer', marginRight: '6px' },
  dangerBtn: { padding: '6px 12px', border: '1px solid #feb2b2', borderRadius: '4px', background: '#fff5f5', fontSize: '13px', cursor: 'pointer', color: '#c53030' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modalCard: { background: '#fff', borderRadius: '12px', padding: '32px', width: '440px' },
  modalTitle: { fontSize: '20px', fontWeight: '700', marginBottom: '20px' },
  label: { display: 'block', fontSize: '14px', fontWeight: '600', color: '#4a5568', marginBottom: '6px', marginTop: '16px' },
  input: { width: '100%', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', outline: 'none' },
  select: { width: '100%', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', outline: 'none', background: '#fff' },
  modalActions: { display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '10px 20px', background: '#edf2f7', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  saveBtn: { padding: '10px 20px', background: '#9C3D97', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  success: { background: '#c6f6d5', color: '#276749', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' },
  error: { background: '#fed7d7', color: '#c53030', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' },
  tempPw: { background: '#fffaf0', border: '1px solid #fbd38d', padding: '16px', borderRadius: '8px', marginBottom: '16px' },
  tempPwLabel: { fontSize: '12px', color: '#975a16', fontWeight: '600', marginBottom: '4px' },
  tempPwValue: { fontSize: '18px', fontFamily: 'monospace', color: '#744210', fontWeight: '700', letterSpacing: '1px' },
};

function formatAZDate(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return isoString.split('T')[0]; }
}

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('grader');
  const [newDeveloper, setNewDeveloper] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [tempPassword, setTempPassword] = useState(null);
  const [copyState, setCopyState] = useState('idle'); // idle | copied | error

  const session = getSession() || {};
  const currentIsDeveloper = session.developer === true;
  const currentEmail = (session.sub || '').toLowerCase();

  async function handleCopyTempPassword() {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword.password);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('error');
    }
  }

  useEffect(() => { loadUsers(); }, []);

  async function loadUsers() {
    try {
      const data = await getUsers();
      setUsers(data.users || []);
    } catch { setError('Failed to load users.'); }
  }

  async function handleCreate() {
    if (!newEmail.trim() || !newName.trim()) return;
    setError('');
    // Developer flag is only sent if the current user is a developer. The
    // backend also guards this, but avoid confusing admins with a checkbox
    // they can't use.
    const devFlag = currentIsDeveloper ? newDeveloper : false;
    const effectiveRole = devFlag ? 'admin' : newRole;
    const result = await createUserAccount(newEmail.trim(), newName.trim(), effectiveRole, devFlag);
    if (result.error) {
      setError(result.error);
    } else {
      setTempPassword({ name: newName, email: newEmail, password: result.temporary_password });
      setShowCreate(false);
      setNewEmail(''); setNewName(''); setNewRole('grader'); setNewDeveloper(false);
      loadUsers();
    }
  }

  async function handleRoleChange(email, currentRole, isDeveloper) {
    // Developers are always admins; this button is hidden for them anyway,
    // but guard just in case.
    if (isDeveloper) return;
    const nextRole = currentRole === 'admin' ? 'grader' : 'admin';
    const result = await updateUserAccount(email, { role: nextRole });
    if (result.error) { setError(result.error); }
    else { setMessage(`${email} is now ${nextRole}.`); loadUsers(); }
  }

  async function handleDeveloperToggle(email, currentlyDev) {
    if (!currentIsDeveloper) return;
    if (currentlyDev && email.toLowerCase() === currentEmail) {
      setError('You cannot revoke your own developer flag. Ask another developer.');
      return;
    }
    const next = !currentlyDev;
    const label = next ? 'grant developer access to' : 'revoke developer access from';
    if (!window.confirm(`Are you sure you want to ${label} ${email}?`)) return;
    setError('');
    const result = await updateUserAccount(email, { developer: next });
    if (result.error) { setError(result.error); }
    else {
      setMessage(next ? `${email} is now a Developer.` : `Developer access revoked for ${email}.`);
      loadUsers();
    }
  }

  async function handleResetPassword(email) {
    const result = await resetUserPassword(email);
    if (result.error) { setError(result.error); }
    else {
      setTempPassword({ name: '', email, password: result.temporary_password });
      setMessage(`Password reset for ${email}.`);
    }
  }

  async function handleDelete(email, name) {
    if (!window.confirm(`Remove ${name} (${email})?`)) return;
    const result = await deleteUserAccount(email);
    if (result.error) { setError(result.error); }
    else { setMessage(result.message); loadUsers(); }
  }

  return (
    <div>
      <h1 style={styles.heading}>User Management</h1>
      {message && <div style={styles.success}>{message}</div>}
      {error && <div style={styles.error}>{error}</div>}

      {tempPassword && (
        <div style={styles.tempPw}>
          <div style={styles.tempPwLabel}>Temporary Password for {tempPassword.email}</div>
          {/* Read-only input + Copy button avoids the old failure mode where
              admins click-dragging across the password div would pick up a
              trailing newline that broke the recipient's manual login. */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
            <input
              type="text"
              readOnly
              value={tempPassword.password}
              onFocus={(e) => e.target.select()}
              style={{
                flex: 1, padding: '10px 12px', border: '1px solid #fbd38d',
                borderRadius: '6px', fontSize: '16px', fontFamily: 'monospace',
                color: '#744210', fontWeight: '700', background: '#fffaf0',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
            <button
              onClick={handleCopyTempPassword}
              style={{
                padding: '10px 16px', background: copyState === 'copied' ? '#276749' : '#9C3D97',
                color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px',
                fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {copyState === 'copied' ? 'Copied' : copyState === 'error' ? 'Copy failed' : 'Copy'}
            </button>
          </div>
          <div style={{ fontSize: '12px', color: '#975a16', marginTop: '8px' }}>
            Share this securely. The user will be required to set a new password before they can use the portal.
          </div>
          <button style={{ ...styles.cancelBtn, marginTop: '8px' }}
            onClick={() => { setTempPassword(null); setCopyState('idle'); }}>Dismiss</button>
        </div>
      )}

      <div style={styles.toolbar}>
        <button style={styles.addBtn} onClick={() => setShowCreate(true)}>+ Add User</button>
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Role</th>
            <th style={styles.th}>Created</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const isDev = u.developer === true;
            const badgeStyle = isDev ? styles.badgeDeveloper : (u.role === 'admin' ? styles.badgeAdmin : styles.badgeGrader);
            const badgeLabel = isDev ? 'developer' : u.role;
            return (
              <tr key={u.email}>
                <td style={styles.td}>{u.name}</td>
                <td style={styles.td}>{u.email}</td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, ...badgeStyle }}>
                    {badgeLabel}
                  </span>
                </td>
                <td style={styles.td}>{formatAZDate(u.created_at)}</td>
                <td style={styles.td}>
                  {!isDev && (
                    <button style={styles.actionBtn} onClick={() => handleRoleChange(u.email, u.role, isDev)}>
                      {u.role === 'admin' ? 'Make Grader' : 'Make Admin'}
                    </button>
                  )}
                  {currentIsDeveloper && (
                    <button
                      style={styles.actionBtn}
                      onClick={() => handleDeveloperToggle(u.email, isDev)}
                      disabled={isDev && u.email.toLowerCase() === currentEmail}
                      title={isDev && u.email.toLowerCase() === currentEmail
                        ? 'Cannot revoke your own developer flag'
                        : (isDev ? 'Revoke developer access' : 'Grant developer access')}
                    >
                      {isDev ? 'Revoke Developer' : 'Make Developer'}
                    </button>
                  )}
                  <button style={styles.actionBtn} onClick={() => handleResetPassword(u.email)}>
                    Reset Password
                  </button>
                  <button style={styles.dangerBtn} onClick={() => handleDelete(u.email, u.name)}>
                    Remove
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Create User Modal */}
      {showCreate && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <h2 style={styles.modalTitle}>Add New User</h2>
            <label style={styles.label}>Full Name</label>
            <input style={styles.input} value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="Jane Doe" autoFocus />
            <label style={styles.label}>Email</label>
            <input style={styles.input} type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              placeholder="jane@hellorache.com" />
            <label style={styles.label}>Role</label>
            <select
              style={{ ...styles.select, ...(newDeveloper ? { background: '#f7fafc', color: '#a0aec0' } : {}) }}
              value={newDeveloper ? 'admin' : newRole}
              onChange={(e) => setNewRole(e.target.value)}
              disabled={newDeveloper}
            >
              <option value="grader">Grader</option>
              <option value="admin">Admin</option>
            </select>
            {currentIsDeveloper && (
              <div style={{
                marginTop: '18px', padding: '12px', background: '#fefcbf',
                border: '1px solid #ecc94b', borderRadius: '6px',
              }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#744210' }}>
                  <input
                    type="checkbox"
                    checked={newDeveloper}
                    onChange={(e) => setNewDeveloper(e.target.checked)}
                    style={{ marginTop: '3px', cursor: 'pointer' }}
                  />
                  <span>
                    <strong>Developer (super admin)</strong>
                    <div style={{ fontSize: '12px', color: '#975a16', marginTop: '4px', lineHeight: '1.4' }}>
                      Grants access to the yellow DEV bar (date override) and the ability to create or revoke other developers. Role is forced to Admin when this is checked.
                    </div>
                  </span>
                </label>
              </div>
            )}
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => { setShowCreate(false); setNewDeveloper(false); }}>Cancel</button>
              <button style={styles.saveBtn} onClick={handleCreate}>Create User</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
